<template>
  <div class="dashboard-page">
    <!-- Header -->
    <header class="dashboard-header">
      <div class="header-content">
        <h1 class="header-title">Dashboard</h1>
        <p class="header-subtitle">Explore proteomics data across human tissues</p>
      </div>
      <div class="header-actions">
        <el-input 
          v-model="searchQuery" 
          placeholder="Search proteins, genes..." 
          prefix-icon="el-icon-search"
          class="search-input"
          clearable
          @keyup.enter.native="handleSearch"
        />
      </div>
    </header>

    <div class="dashboard-content">
      <!-- Stats Row -->
      <div class="stats-row">
        <div class="stat-card" v-for="stat in statsCards" :key="stat.label">
          <div class="stat-icon" :style="{ background: stat.gradient }">
            <i :class="stat.icon"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ stat.value }}</span>
            <span class="stat-label">{{ stat.label }}</span>
          </div>
        </div>
      </div>

      <!-- Main Content Grid -->
      <div class="main-grid">
        <!-- Left Sidebar -->
        <div class="sidebar-section">
          <!-- Quick Actions -->
          <div class="card quick-actions">
            <h3>Quick Actions</h3>
            <div class="action-grid">
              <div class="action-item" @click="$router.push('/projects')">
                <i class="el-icon-search"></i>
                <span>Browse Projects</span>
              </div>
              <div class="action-item" @click="$router.push('/gene-browser')">
                <i class="el-icon-s-data"></i>
                <span>Search Protein</span>
              </div>
              <div class="action-item" @click="$router.push('/drug-target-analysis')">
                <i class="el-icon-magic-stick"></i>
                <span>Drug Targets</span>
              </div>
              <div class="action-item" @click="$router.push('/download')">
                <i class="el-icon-download"></i>
                <span>Download Data</span>
              </div>
            </div>
          </div>

          <!-- Recent Projects -->
          <div class="card recent-projects">
            <div class="card-header">
              <h3>Recent Projects</h3>
              <el-button type="text" size="small" @click="$router.push('/projects')">View All</el-button>
            </div>
            <div class="project-list">
              <div 
                v-for="project in recentProjects" 
                :key="project.datasetid" 
                class="project-item"
                @click="$router.push(`/project/${project.datasetid}`)"
              >
                <div class="project-info">
                  <span class="project-name">{{ project.name }}</span>
                  <span class="project-meta">{{ project.tissueType }} · {{ project.samples }} samples</span>
                </div>
                <i class="el-icon-arrow-right"></i>
              </div>
              <div v-if="recentProjects.length === 0" class="empty-state">
                <i class="el-icon-folder-opened"></i>
                <p>No projects yet</p>
              </div>
            </div>
          </div>

          <!-- Platform Stats -->
          <div class="card platform-stats">
            <h3>Platform Statistics</h3>
            <div class="platform-stat-list">
              <div class="platform-stat-item">
                <span class="label">Total Proteins</span>
                <span class="value">{{ platformStats.totalProteins.toLocaleString() }}</span>
              </div>
              <div class="platform-stat-item">
                <span class="label">Total Samples</span>
                <span class="value">{{ platformStats.totalSamples.toLocaleString() }}</span>
              </div>
              <div class="platform-stat-item">
                <span class="label">Tissue Types</span>
                <span class="value">{{ platformStats.tissueTypes }}</span>
              </div>
              <div class="platform-stat-item">
                <span class="label">Cancer Types</span>
                <span class="value">{{ platformStats.cancerTypes }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Human Body Section -->
        <div class="body-section">
          <div class="section-header">
            <h2>Tissue Atlas</h2>
            <p>Explore datasets across different tissue types</p>
          </div>
          
          <div class="body-content">
            <!-- Tissue Bar Chart -->
            <div class="tissue-chart">
              <div class="chart-title">Datasets by Tissue</div>
              <div class="bar-chart">
                <div 
                  v-for="item in tissueStats" 
                  :key="item.tissue" 
                  class="bar-item"
                  @click="navigateToTissue(item.tissue)"
                >
                  <span class="bar-label">{{ item.tissue }}</span>
                  <div class="bar-track">
                    <div 
                      class="bar-fill" 
                      :style="{ width: getBarWidth(item.count) + '%' }"
                    ></div>
                  </div>
                  <span class="bar-count">{{ item.count }}</span>
                </div>
                <div v-if="tissueStats.length === 0" class="chart-empty">
                  <i class="el-icon-loading" v-if="loadingTissueStats"></i>
                  <span v-else>No tissue data available</span>
                </div>
              </div>
            </div>

            <!-- Body Viewer -->
            <div class="body-viewer-container">
              <BodySvgViewer
                :image-src="require('@/assets/Shape.png')"
                :scale="0.35"
                @path-hover="handleBodyHover"
                @path-click="handleBodyClick"
                @path-leave="handleBodyLeave"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import BodySvgViewer from '@/components/Widgets/BodySvgViewer.vue'

export default {
  name: 'DashboardView',
  components: {
    BodySvgViewer
  },
  data() {
    return {
      searchQuery: '',
      hoveredTissue: null,
      selectedTissue: null,
      projects: [],
      tissueStats: [],
      loadingTissueStats: false,
      maxTissueCount: 1,
      platformStats: {
        totalProteins: 0,
        totalSamples: 0,
        tissueTypes: 0,
        cancerTypes: 0
      }
    }
  },
  computed: {
    statsCards() {
      return [
        { label: 'Total Projects', value: this.projects.length, icon: 'el-icon-folder', gradient: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
        { label: 'Public Datasets', value: this.projects.filter(p => p.datasetType === 'public').length, icon: 'el-icon-view', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
        { label: 'Private Datasets', value: this.projects.filter(p => p.datasetType === 'private').length, icon: 'el-icon-lock', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' },
        { label: 'Differential Data', value: this.projects.filter(p => p.datasetType === 'differential').length, icon: 'el-icon-s-data', gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }
      ]
    },
    recentProjects() {
      return this.projects.slice(0, 5)
    }
  },
  mounted() {
    this.fetchProjects()
    this.fetchTissueStats()
  },
  methods: {
    hoverTissue(tissue) {
      this.hoveredTissue = tissue
    },
    selectTissue(tissue) {
      this.selectedTissue = tissue
      this.$router.push({ path: '/projects', query: { tissue } })
    },
    navigateToTissue(tissue) {
      this.$router.push({ path: '/projects', query: { tissue } })
    },
    getBarWidth(count) {
      return Math.max(5, (count / this.maxTissueCount) * 100)
    },
    handleSearch() {
      if (this.searchQuery.trim()) {
        this.$router.push({ path: '/gene-browser', query: { q: this.searchQuery } })
      }
    },
    async fetchProjects() {
      try {
        const resp = await axios.post('/api/projectSearch/list_projects')
        if (resp && resp.data && resp.data.code === 200) {
          const rows = Array.isArray(resp.data.data) ? resp.data.data : []
          
          this.projects = rows.map(r => ({
            datasetid: r.datasetid || r.datasetId || '',
            name: r.name || '',
            description: r.description || '',
            pubmedId: r.pubmedId ?? null,
            samples: r.samples ?? 0,
            cancerType: r.cancerType || '',
            tissueType: r.tissueType || '',
            datasetType: r.datasetType || 'public',
            views: r.views ?? 0,
          }))
          
          // Calculate platform stats
          this.platformStats.totalSamples = this.projects.reduce((sum, p) => sum + (p.samples || 0), 0)
          this.platformStats.totalProteins = this.projects.length * 3000 // Estimate
          this.platformStats.tissueTypes = new Set(this.projects.map(p => p.tissueType).filter(Boolean)).size
          this.platformStats.cancerTypes = new Set(this.projects.map(p => p.cancerType).filter(Boolean)).size
        }
      } catch (e) {
        console.error('Error loading projects:', e)
      }
    },
    async fetchTissueStats() {
      this.loadingTissueStats = true
      try {
        const resp = await axios.post('/api/projectSearch/get_tissue_stats')
        if (resp && resp.data && resp.data.code === 200) {
          this.tissueStats = resp.data.data || []
          this.maxTissueCount = Math.max(1, ...this.tissueStats.map(t => t.count))
        }
      } catch (e) {
        console.error('Error loading tissue stats:', e)
        this.tissueStats = []
      } finally {
        this.loadingTissueStats = false
      }
    },
    // Body SVG Viewer event handlers
    handleBodyHover({ label }) {
      console.log('Hovering:', label)
      // You can add custom logic here, e.g., highlight related data
    },
    handleBodyClick({ label }) {
      console.log('Clicked:', label)
      // Navigate or show details based on the clicked region
      // Example: parse label to extract tissue name and navigate
      if (label) {
        const tissueName = label.split(':')[0]
        this.$router.push({ path: '/projects', query: { tissue: tissueName } })
      }
    },
    handleBodyLeave() {
      // Handle mouse leave if needed
    }
  }
}
</script>

<style scoped>
.dashboard-page {
  min-height: 100vh;
  background: #f8fafc;
}

/* Header */
.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
}

.header-title {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px;
}

.header-subtitle {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.search-input {
  width: 300px;
}

.search-input >>> .el-input__inner {
  border-radius: 8px;
  border-color: #e2e8f0;
}

/* Content */
.dashboard-content {
  padding: 24px 32px;
}

/* Stats Row */
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}

.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  transition: transform 0.2s, box-shadow 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 22px;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
}

.stat-label {
  font-size: 13px;
  color: #64748b;
}

/* Main Grid */
.main-grid {
  display: grid;
  grid-template-columns: 480px 1fr;
  gap: 24px;
}

/* Body Section */
.body-section {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

.section-header {
  margin-bottom: 20px;
}

.section-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px;
}

.section-header p {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.body-content {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.tissue-chart {
  flex: 0 0 320px;
  min-width: 300px;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
}

.bar-chart {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bar-item {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 6px 0;
  transition: all 0.2s ease;
}

.bar-item:hover {
  transform: translateX(3px);
}

.bar-item:hover .bar-fill {
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
}

.bar-label {
  flex: 0 0 120px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

.bar-track {
  flex: 1;
  height: 12px;
  background: #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%);
  border-radius: 6px;
  transition: all 0.3s ease;
}

.bar-count {
  flex: 0 0 32px;
  font-size: 14px;
  font-weight: 600;
  color: #3b82f6;
  text-align: right;
}

.chart-empty {
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
  padding: 20px 0;
}

.body-viewer-container {
  flex: 1;
  display: flex;
  justify-content: center;
}

/* Sidebar Section */
.sidebar-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

.card h3 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 16px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.card-header h3 {
  margin: 0;
}

/* Quick Actions */
.action-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.action-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 12px;
  background: #f8fafc;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.action-item:hover {
  background: #e0e7ff;
  color: #4f46e5;
}

.action-item i {
  font-size: 24px;
  color: #4f46e5;
}

.action-item span {
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  text-align: center;
}

.action-item:hover span {
  color: #4f46e5;
}

/* Recent Projects */
.project-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.project-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.project-item:hover {
  background: #e0e7ff;
}

.project-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
}

.project-name {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.project-meta {
  font-size: 12px;
  color: #64748b;
}

.project-item i {
  color: #94a3b8;
  flex-shrink: 0;
}

.empty-state {
  padding: 24px;
  text-align: center;
  color: #94a3b8;
}

.empty-state i {
  font-size: 32px;
  margin-bottom: 8px;
}

/* Platform Stats */
.platform-stat-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.platform-stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
}

.platform-stat-item .label {
  font-size: 13px;
  color: #64748b;
}

.platform-stat-item .value {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Responsive */
@media (max-width: 1200px) {
  .main-grid {
    grid-template-columns: 1fr;
  }
  
  .body-content {
    flex-direction: column;
  }
  
  .tissue-chart {
    flex: none;
    width: 100%;
    max-width: 400px;
    margin: 0 auto;
  }
}

@media (max-width: 768px) {
  .dashboard-header {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
  
  .search-input {
    width: 100%;
  }
  
  .stats-row {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .dashboard-content {
    padding: 16px;
  }
}
</style>
